import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../../core/common/header/PageHeader";
import {
  FaHome,
  FaEdit,
  FaSpinner,
  FaSave,
  FaCheckCircle,
  FaRobot,
  FaUser,
  FaBook,
  FaHeadphones,
  FaPencilAlt,
} from "react-icons/fa";
import QuestionDisplay from "../../exam/QuestionDisplay";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

type QuestionType =
  | "MULTIPLE_CHOICE"
  | "TRUE_FALSE"
  | "TEXT_INPUT"
  | "ESSAY"
  | "EMAIL"
  | "LETTER"
  | "AUDIO_MULTIPLE_CHOICE"
  | "MATCHING"
  | "DRAG_DROP"
  | "FILL_BLANKS"
  | "SENTENCE_ORDER";

type Answer = {
  id: number;
  questionId: number;
  questionNumber: number;
  questionText: string;
  questionType: QuestionType;
  module: string;
  studentAnswer: string;
  correctAnswer: string;
  points: number;
  score: number | null;
  feedback: string | null;
  isGraded: boolean;
  contextText?: string;
  options?: string[];
  audioUrl?: string | null;
};

type Attempt = {
  id: number;
  studentId: number;
  studentName: string;
  studentEmail: string;
  examSetName: string;
  level: string;
  submittedAt: string;
  totalScore: number;
  maxScore: number;
  overallFeedback: string | null;
};

const MentorGradeExam: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();

  const [attempts, setAttempts] = useState<Attempt[]>([]);
  const [selectedAttempt, setSelectedAttempt] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [overallFeedback, setOverallFeedback] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiEvaluating, setAiEvaluating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchAttempts();
  }, [assignmentId]);

  useEffect(() => {
    if (selectedAttempt) {
      fetchAnswers(selectedAttempt);
    }
  }, [selectedAttempt]);

  const fetchAttempts = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/exams/assignments/${assignmentId}/attempts`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAttempts(data);
        if (data.length > 0 && !selectedAttempt) {
          setSelectedAttempt(data[0].id);
        }
      } else {
        setError("Failed to load attempts");
      }
    } catch (err) {
      console.error("Error fetching attempts:", err);
      setError("Network error");
    } finally {
      setLoading(false);
    }
  };

  const fetchAnswers = async (attemptId: number) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/exams/attempts/${attemptId}/answers`,
        {
          credentials: "include",
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAnswers(data);
        const attempt = attempts.find((a) => a.id === attemptId);
        setOverallFeedback(attempt?.overallFeedback || "");
      }
    } catch (err) {
      console.error("Error fetching answers:", err);
    }
  };

  const handleGradeAnswer = async (
    answerId: number,
    score: number,
    feedback: string
  ) => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/exams/answers/${answerId}/grade`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ score, feedback }),
        }
      );

      if (response.ok) {
        setSuccess("Answer graded successfully");
        await fetchAnswers(selectedAttempt!);
        await fetchAttempts();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to grade answer");
      }
    } catch (err) {
      console.error("Error grading answer:", err);
      setError("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleAIEvaluate = async (attemptId: number) => {
    if (
      !window.confirm(
        "Use AI to evaluate all writing answers (ESSAY, EMAIL, LETTER)?"
      )
    ) {
      return;
    }

    setAiEvaluating(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/exams/attempts/${attemptId}/evaluate-ai-batch`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      if (response.ok) {
        setSuccess("AI evaluation completed successfully");
        await fetchAnswers(attemptId);
        await fetchAttempts();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to evaluate with AI");
      }
    } catch (err) {
      console.error("Error evaluating with AI:", err);
      setError("Network error");
    } finally {
      setAiEvaluating(false);
    }
  };

  const handleSaveOverallFeedback = async () => {
    if (!selectedAttempt) return;

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/mentor/exams/attempts/${selectedAttempt}/feedback`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ feedback: overallFeedback }),
        }
      );

      if (response.ok) {
        setSuccess("Overall feedback saved");
        await fetchAttempts();
        setTimeout(() => setSuccess(""), 3000);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to save feedback");
      }
    } catch (err) {
      console.error("Error saving feedback:", err);
      setError("Network error");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getModuleIcon = (module: string) => {
    switch (module) {
      case "LESEN":
        return <FaBook className="text-blue-600" />;
      case "HOREN":
        return <FaHeadphones className="text-purple-600" />;
      case "SCHREIBEN":
        return <FaPencilAlt className="text-green-600" />;
      default:
        return null;
    }
  };

  const currentAttempt = attempts.find((a) => a.id === selectedAttempt);

  // Group answers by module
  const answersByModule = answers.reduce((acc, answer) => {
    if (!acc[answer.module]) {
      acc[answer.module] = [];
    }
    acc[answer.module].push(answer);
    return acc;
  }, {} as Record<string, Answer[]>);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <FaSpinner className="animate-spin text-[#009fe3] text-4xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <PageHeader
        icon={<FaEdit />}
        title="Grade Exam"
        breadcrumb={[
          { label: "Home", to: "/" },
          { label: "Dashboard", to: "/mentor/dashboard" },
          { label: "Exams", to: "/mentor/exam-management" },
          { label: "Grade" },
        ]}
      />

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Messages */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <FaCheckCircle />
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Student List Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-4 border border-gray-200 sticky top-4">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <FaUser className="text-[#009fe3]" />
                Students ({attempts.length})
              </h3>

              {attempts.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  No submissions yet
                </p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {attempts.map((attempt) => {
                    const pendingCount = answers.filter(
                      (a) => !a.isGraded
                    ).length;
                    return (
                      <button
                        key={attempt.id}
                        onClick={() => setSelectedAttempt(attempt.id)}
                        className={`w-full text-left p-3 rounded-lg transition-all ${
                          selectedAttempt === attempt.id
                            ? "bg-[#009fe3] text-white shadow-md"
                            : "bg-gray-50 text-gray-900 hover:bg-gray-100"
                        }`}
                      >
                        <div className="font-semibold text-sm">
                          {attempt.studentName}
                        </div>
                        <div
                          className={`text-xs mt-1 ${
                            selectedAttempt === attempt.id
                              ? "text-white/80"
                              : "text-gray-500"
                          }`}
                        >
                          {formatDate(attempt.submittedAt)}
                        </div>
                        {selectedAttempt === attempt.id && pendingCount > 0 && (
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-orange-500 text-white">
                              {pendingCount} pending
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Grading Area */}
          <div className="lg:col-span-3">
            {!currentAttempt ? (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <FaUser className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                <p className="text-gray-500">
                  Select a student to start grading
                </p>
              </div>
            ) : (
              <>
                {/* Student Info Card */}
                <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {currentAttempt.studentName}
                      </h2>
                      <p className="text-sm text-gray-600">
                        {currentAttempt.studentEmail}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Submitted: {formatDate(currentAttempt.submittedAt)}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-bold text-[#009fe3]">
                        {currentAttempt.totalScore}/{currentAttempt.maxScore}
                      </div>
                      <p className="text-sm text-gray-600">
                        {(
                          (currentAttempt.totalScore /
                            currentAttempt.maxScore) *
                          100
                        ).toFixed(1)}
                        %
                      </p>
                    </div>
                  </div>

                  {/* AI Evaluate Button */}
                  <button
                    onClick={() => handleAIEvaluate(currentAttempt.id)}
                    disabled={aiEvaluating}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50 transition-all"
                  >
                    {aiEvaluating ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Evaluating with AI...
                      </>
                    ) : (
                      <>
                        <FaRobot />
                        AI Evaluate All Writing Answers
                      </>
                    )}
                  </button>
                </div>

                {/* Answers by Module */}
                {Object.entries(answersByModule).map(
                  ([module, moduleAnswers]) => {
                    return (
                      <div
                        key={module}
                        className="bg-white rounded-xl shadow-md p-6 border border-gray-200 mb-6"
                      >
                        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                          {getModuleIcon(module)}
                          {module} ({moduleAnswers.length} questions)
                        </h3>

                        <div className="space-y-6">
                          {moduleAnswers.map((answer) => (
                            <div
                              key={answer.id}
                              className={`border-l-4 ${
                                answer.isGraded
                                  ? "border-green-500 bg-green-50"
                                  : "border-orange-500 bg-orange-50"
                              } p-4 rounded-r-lg`}
                            >
                              {/* Question Display Component */}
                              <QuestionDisplay
                                question={{
                                  id: answer.questionId,
                                  questionNumber: answer.questionNumber,
                                  questionType: answer.questionType,
                                  questionText: answer.questionText,
                                  points: answer.points,
                                  contextText: answer.contextText || "",
                                  options: answer.options || [],
                                  correctAnswer: answer.correctAnswer,
                                  audioUrl: answer.audioUrl || undefined,
                                  module: answer.module,
                                }}
                                answer={answer.studentAnswer}
                                onAnswerChange={() => {}} // Read-only
                                disabled={true}
                                showCorrectAnswer={
                                  !["ESSAY", "EMAIL", "LETTER"].includes(
                                    answer.questionType
                                  )
                                }
                                correctAnswer={answer.correctAnswer}
                              />

                              {/* Grading Form */}
                              <div className="mt-4">
                                <GradingForm
                                  answer={answer}
                                  onSave={handleGradeAnswer}
                                  saving={saving}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                )}

                {/* Overall Feedback */}
                <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    Overall Feedback
                  </h3>
                  <textarea
                    value={overallFeedback}
                    onChange={(e) => setOverallFeedback(e.target.value)}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent"
                    placeholder="Provide overall feedback for this student's exam performance..."
                    style={{ fontSize: "16px" }}
                  />
                  <button
                    onClick={handleSaveOverallFeedback}
                    disabled={saving}
                    className="mt-4 inline-flex items-center gap-2 px-6 py-3 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 disabled:opacity-50 transition-colors"
                  >
                    {saving ? (
                      <>
                        <FaSpinner className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave />
                        Save Overall Feedback
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

// Grading Form Component
const GradingForm: React.FC<{
  answer: Answer;
  onSave: (answerId: number, score: number, feedback: string) => void;
  saving: boolean;
}> = ({ answer, onSave, saving }) => {
  const [score, setScore] = useState(answer.score?.toString() || "");
  const [feedback, setFeedback] = useState(answer.feedback || "");
  const [isEditing, setIsEditing] = useState(!answer.isGraded);

  const handleSubmit = () => {
    onSave(answer.id, parseFloat(score) || 0, feedback);
    setIsEditing(false);
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          Score (out of {answer.points}):
        </label>
        <input
          type="number"
          value={score}
          onChange={(e) => setScore(e.target.value)}
          min="0"
          max={answer.points}
          step="0.5"
          disabled={!isEditing}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] disabled:bg-gray-100"
          style={{ fontSize: "16px" }}
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">
          Feedback:
        </label>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={3}
          disabled={!isEditing}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] disabled:bg-gray-100"
          placeholder="Provide feedback for this answer..."
          style={{ fontSize: "16px" }}
        />
      </div>

      <div className="flex gap-2">
        {isEditing ? (
          <>
            <button
              onClick={handleSubmit}
              disabled={saving || !score}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <FaSave />
              Save Grade
            </button>
            {answer.isGraded && (
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            )}
          </>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg font-semibold hover:bg-cyan-600 transition-colors"
          >
            <FaEdit />
            Edit Grade
          </button>
        )}
      </div>
    </div>
  );
};

export default MentorGradeExam;
